import Alpine from 'alpinejs';
import Quill from 'quill';
import { getCSRFToken } from '../utils/getCSRFToken';

export default function registerCalendarSettingsOnlineStoreStore() {
  const existing = Alpine.store('calendarSettingsOnlineStoreStore');
  if (existing) return existing;

  const baseStore = Alpine.store('calendarSettingsStore');
  if (!baseStore) return null;

  const onlineStoreStore = {};
  Object.defineProperties(
    onlineStoreStore,
    Object.getOwnPropertyDescriptors(baseStore),
  );

  const onlineStoreExtensions = {
    depositOptions: [],
    bookingMode: null,
    previousBookingMode: null,
    pendingBookingMode: null,
    showBookingModeConfirmModal: false,
    onlineStoreInitialized: false,
    onlineStoreInitPromise: null,
    showTermsModal: false,
    termsContent: '',
    quill: null,
    updatedTermsContent: null,
    async init() {
      if (this.onlineStoreInitialized) return;
      if (this.onlineStoreInitPromise) return this.onlineStoreInitPromise;

      this.onlineStoreInitPromise = (async () => {
        this.initializeSettingsContext();
        this.fetchStripeAccountStatus();
        this.loadDepositOptions();
        this.initializeStoreCustomization();
        this.initLogoPreview();
        this.initBookingModeWatcher();

        // Terms modal Quill editor is rendered on the section page but needs explicit init here.
        setTimeout(() => {
          const editor = document.querySelector('[x-ref="termsQuillEditor"]');
          if (editor) {
            this.initQuill(editor);
          }
        }, 100);

        this.onlineStoreInitialized = true;
      })();

      try {
        return await this.onlineStoreInitPromise;
      } finally {
        this.onlineStoreInitPromise = null;
      }
    },

    openTermsModal(initialContent) {
      this.termsContent = this.updatedTermsContent || initialContent;
      this.showTermsModal = true;

      if (this.quill) {
        this.quill.root.innerHTML = this.termsContent;
      }
    },

    closeTermsModal() {
      // Avoid mutating Quill while the modal is tearing down; this can
      // trigger Alpine/Quill race errors when the editor node is detached.
      this.showTermsModal = false;
      this.termsContent = '';
    },

    initQuill(element) {
      if (this.quill || !element) return;

      const toolbarOptions = [
        [{ header: [4, 5, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ];

      const initQuillInstance = () => {
        try {
          this.quill = new Quill(element, {
            theme: 'snow',
            modules: {
              toolbar: toolbarOptions,
              clipboard: {
                matchVisual: false,
              },
            },
            formats: ['header', 'bold', 'italic', 'underline', 'list'],
          });

          setTimeout(() => {
            this.quill.root.innerHTML = this.termsContent || '';
            this.quill.focus();
          }, 0);

          this.quill.on('text-change', () => {
            this.termsContent = this.quill.root.innerHTML;
          });
        } catch (error) {
          console.error('Error initializing Quill:', error);
        }
      };

      initQuillInstance();
      if (!this.quill) {
        setTimeout(initQuillInstance, 100);
      }
    },

    async saveTerms() {
      if (!this.calendarId) {
        return;
      }

      this.isLoading = true;
      try {
        const response = await fetch(
          `/calendar/${this.calendarId}/update-terms/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              terms_and_conditions: this.termsContent,
            }),
          },
        );

        const data = await response.json();
        if (!response.ok) {
          throw new Error(
            data.error || 'Failed to update terms and conditions',
          );
        }

        this.updatedTermsContent = this.termsContent;
        this.showTermsModal = false;
        const toastStore = Alpine.store('toastStore');
        if (toastStore && toastStore.addToast) {
          toastStore.addToast(
            'Terms and conditions updated successfully',
            'success',
          );
        }
      } catch (error) {
        const toastStore = Alpine.store('toastStore');
        if (toastStore && toastStore.addToast) {
          toastStore.addToast(
            error.message || 'An error occurred while updating terms',
            'danger',
          );
        }
      } finally {
        this.isLoading = false;
      }
    },

    handleBookingModeChange(newValue, selectElement) {
      if (this.previousBookingMode !== newValue) {
        this.pendingBookingMode = newValue;

        if (selectElement) {
          selectElement.value = this.previousBookingMode;
        }

        this.showBookingModeConfirmModal = true;
      }
    },

    confirmBookingModeChange() {
      this.previousBookingMode = this.pendingBookingMode;
      this.bookingMode = this.pendingBookingMode;
      this.showBookingModeConfirmModal = false;

      setTimeout(() => {
        const form = document.querySelector('form#online-store-settings-form');
        if (form) {
          const submitIdentifier = document.createElement('input');
          submitIdentifier.type = 'hidden';
          submitIdentifier.name = 'update_online_store_settings';
          submitIdentifier.value = '1';
          form.appendChild(submitIdentifier);
          form.submit();
        }
      }, 0);
    },

    cancelBookingModeChange() {
      this.bookingMode = this.previousBookingMode;
      this.showBookingModeConfirmModal = false;
    },

    getBookingModeConfirmMessage() {
      if (this.pendingBookingMode === 'RO') {
        return 'This will disable <strong>Instant Booking</strong> for all Listings (Request Mode only).';
      } else if (this.previousBookingMode === 'RO') {
        return 'This will enable <strong>Instant Booking</strong> for all Listings. You can then set individual Listing preferences in Listing Settings.';
      }
      return 'Confirm this change to update your booking mode. Your instant booking settings will remain unchanged.';
    },

    loadDepositOptions() {
      if (!this.depositOptions) {
        this.depositOptions = [];
      }

      try {
        const container = document.getElementById('calendarSettingsContainer');
        if (container && container.dataset.depositOptions) {
          const parsedOptions = JSON.parse(container.dataset.depositOptions);
          if (Array.isArray(parsedOptions)) {
            this.depositOptions = parsedOptions;
          }
        }

        if (!this.depositOptions.length) {
          this.depositOptions = [{ percentage: 100, is_default: true }];
        }
      } catch (error) {
        console.error('Error loading deposit options:', error);
        this.depositOptions = [{ percentage: 100, is_default: true }];
      }
    },

    addDepositOption() {
      if (!this.depositOptions) {
        this.depositOptions = [];
      }

      this.depositOptions.push({
        percentage: 50,
        is_default: false,
      });
    },

    setDefaultDepositOption(index) {
      if (!this.depositOptions) return;
      this.depositOptions.forEach((option, i) => {
        option.is_default = i === index;
      });
    },

    removeDepositOption(index) {
      if (!this.depositOptions) return;

      if (this.depositOptions.length > 1) {
        const wasDefault = this.depositOptions[index].is_default;
        this.depositOptions.splice(index, 1);
        if (wasDefault && this.depositOptions.length > 0) {
          this.depositOptions[0].is_default = true;
        }
      }
    },

    initBookingModeWatcher() {
      const bookingModeSelect = document.querySelector('[name=booking_mode]');
      if (bookingModeSelect) {
        bookingModeSelect.addEventListener('change', () => {
          const depositOptionsSection = document.getElementById(
            'depositOptionsSection',
          );
          if (depositOptionsSection) {
            if (bookingModeSelect.value === 'RO') {
              depositOptionsSection.style.display = 'none';
            } else {
              depositOptionsSection.style.display = 'block';
            }
          }
        });
      }
    },
  };

  Object.defineProperties(
    onlineStoreStore,
    Object.getOwnPropertyDescriptors(onlineStoreExtensions),
  );

  return Alpine.store('calendarSettingsOnlineStoreStore', onlineStoreStore);
}
